export type CheckStatus = 'pending' | 'running' | 'passed' | 'failed' | 'skipped';

export interface AudioCheckStep {
  id: string;
  label: string;
  description: string;
  status: CheckStatus;
  detail?: string;
}

export const INITIAL_AUDIO_CHECK_STEPS: AudioCheckStep[] = [
  {
    id: 'browser',
    label: 'Browser compatibility',
    description: 'Verify recording APIs are available',
    status: 'pending',
  },
  {
    id: 'microphone',
    label: 'Microphone access',
    description: 'Request permission and open your mic',
    status: 'pending',
  },
  {
    id: 'mic-level',
    label: 'Microphone input',
    description: 'Detect audio from your microphone',
    status: 'pending',
  },
  {
    id: 'speaker',
    label: 'Speaker output',
    description: 'Play a short test tone',
    status: 'pending',
  },
];

export interface RunAudioChecksOptions {
  /**
   * Promise started synchronously inside a click handler.
   * Chrome/Edge require getUserMedia to begin during user activation;
   * starting it later (after React setState / delays) often yields NotAllowedError
   * even when the site already has microphone "Allowed".
   */
  micPromise: Promise<MediaStream>;
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${label} timed out`)), ms);
    promise
      .then(value => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch(err => {
        clearTimeout(timer);
        reject(err);
      });
  });
}

function checkBrowserSupport(): { ok: boolean; detail?: string } {
  if (typeof window === 'undefined' || !window.isSecureContext) {
    return {
      ok: false,
      detail: 'Microphone access requires a secure (HTTPS) connection.',
    };
  }
  if (!navigator.mediaDevices?.getUserMedia) {
    return { ok: false, detail: 'Your browser does not support microphone access.' };
  }
  if (typeof MediaRecorder === 'undefined') {
    return { ok: false, detail: 'Recording is not supported in this browser.' };
  }
  return { ok: true, detail: 'Browser supports recording.' };
}

async function queryMicPermissionState(): Promise<PermissionState | 'unknown'> {
  try {
    if (!navigator.permissions?.query) return 'unknown';
    // Some browsers type-check PermissionName narrowly; cast for microphone.
    const status = await navigator.permissions.query({
      name: 'microphone' as PermissionName,
    });
    return status.state;
  } catch {
    return 'unknown';
  }
}

function micErrorDetail(err: unknown, permissionState: PermissionState | 'unknown'): string {
  const name = err instanceof DOMException ? err.name : err instanceof Error ? err.name : '';
  const message = err instanceof Error ? err.message : String(err ?? '');
  const codeHint = name ? ` [${name}]` : '';

  switch (name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
      if (permissionState === 'granted') {
        return (
          `Browser reported microphone as allowed, but opening it still failed${codeHint}. ` +
          'Close other apps/tabs using the mic, confirm Windows Privacy → Microphone is on for desktop apps, then tap Retry.'
        );
      }
      if (permissionState === 'denied') {
        return (
          `Microphone is blocked for this site${codeHint}. ` +
          'Click the lock/tune icon in the address bar → Permissions → Microphone → Allow, then tap Retry.'
        );
      }
      return (
        `Microphone request was not allowed${codeHint}. ` +
        'Tap Retry and choose Allow if prompted. If the prompt never appears, set Microphone to Allow for this site in the address-bar permissions.'
      );
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return `No microphone was found${codeHint}. Connect a mic and try again.`;
    case 'NotReadableError':
    case 'TrackStartError':
      return (
        `Microphone is busy or could not be opened${codeHint}. ` +
        'Close Zoom/Teams/other tabs using the mic, then tap Retry.'
      );
    case 'OverconstrainedError':
    case 'ConstraintNotSatisfiedError':
      return `Could not open your microphone with the requested settings${codeHint}. Tap Retry.`;
    case 'AbortError':
      return `Microphone request was interrupted${codeHint}. Tap Retry.`;
    case 'SecurityError':
      return `Browser blocked microphone access${codeHint}. Use HTTPS/localhost and allow mic for this site.`;
    case 'TypeError':
      return `Microphone API error${codeHint}${message ? `: ${message}` : ''}. Refresh the page and try again.`;
    default:
      break;
  }

  return message
    ? `Could not access microphone${codeHint}: ${message}`
    : `Could not access microphone${codeHint}. Tap Retry.`;
}

/**
 * Start getUserMedia in the same synchronous turn as a click.
 * Call this from onClick BEFORE any await / React state updates that schedule work.
 */
export function beginMicrophoneRequest(): Promise<MediaStream> {
  if (!navigator.mediaDevices?.getUserMedia) {
    return Promise.reject(
      new DOMException('getUserMedia is not available', 'NotSupportedError')
    );
  }
  // Keep constraints minimal — advanced constraints can fail oddly on some
  // Chromium + Intel Smart Sound setups even when permission is Allowed.
  return navigator.mediaDevices.getUserMedia({ audio: true });
}

async function resolveMicrophoneStream(
  micPromise: Promise<MediaStream>
): Promise<{ ok: boolean; detail?: string; stream?: MediaStream }> {
  const permissionState = await queryMicPermissionState();

  try {
    const stream = await micPromise;
    const tracks = stream.getAudioTracks();
    if (tracks.length === 0) {
      stream.getTracks().forEach(t => t.stop());
      return { ok: false, detail: 'No microphone device found.' };
    }
    return { ok: true, detail: 'Microphone access granted.', stream };
  } catch (firstErr) {
    // One retry with a fresh basic request — helps when the first promise was
    // started in a bad activation state or aborted by the browser.
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const tracks = stream.getAudioTracks();
      if (tracks.length === 0) {
        stream.getTracks().forEach(t => t.stop());
        return { ok: false, detail: 'No microphone device found.' };
      }
      return { ok: true, detail: 'Microphone access granted.', stream };
    } catch (secondErr) {
      return { ok: false, detail: micErrorDetail(secondErr || firstErr, permissionState) };
    }
  }
}

function createAudioContext(): AudioContext {
  const Ctor =
    window.AudioContext ||
    (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) {
    throw new Error('AudioContext not supported');
  }
  return new Ctor();
}

async function ensureAudioContextRunning(ctx: AudioContext): Promise<boolean> {
  try {
    if (ctx.state === 'suspended') {
      await withTimeout(ctx.resume(), 1500, 'AudioContext resume');
      await delay(50);
    }
    return ctx.state === 'running';
  } catch {
    return false;
  }
}

function checkMicrophoneTrackLive(stream: MediaStream): { ok: boolean; detail?: string } {
  const track = stream.getAudioTracks()[0];
  if (!track) {
    return { ok: false, detail: 'No microphone device found.' };
  }
  if (track.readyState !== 'live') {
    return { ok: false, detail: 'Microphone is not active. Check your input device and retry.' };
  }
  if (!track.enabled) {
    return { ok: false, detail: 'Microphone is disabled. Enable it in browser or system settings.' };
  }
  return { ok: true, detail: 'Microphone is connected and active.' };
}

async function measureMicrophonePeak(stream: MediaStream): Promise<number> {
  const audioContext = createAudioContext();
  const running = await ensureAudioContextRunning(audioContext);
  if (!running) {
    await audioContext.close().catch(() => {});
    return -1;
  }

  try {
    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.3;
    source.connect(analyser);

    const data = new Uint8Array(analyser.fftSize);
    const samples = 20;
    const intervalMs = 100;
    let peak = 0;

    await delay(150);

    for (let i = 0; i < samples; i++) {
      analyser.getByteTimeDomainData(data);
      let samplePeak = 0;
      for (let j = 0; j < data.length; j++) {
        const amp = Math.abs(data[j] - 128);
        if (amp > samplePeak) samplePeak = amp;
      }
      peak = Math.max(peak, samplePeak);
      await delay(intervalMs);
    }

    source.disconnect();
    return peak;
  } finally {
    await audioContext.close().catch(() => {});
  }
}

async function checkMicrophoneLevel(stream: MediaStream): Promise<{ ok: boolean; detail?: string }> {
  const trackCheck = checkMicrophoneTrackLive(stream);
  if (!trackCheck.ok) {
    return trackCheck;
  }

  try {
    const peak = await withTimeout(measureMicrophonePeak(stream), 4000, 'Microphone level test');

    if (peak < 0) {
      return {
        ok: true,
        detail:
          'Microphone access OK (level test skipped — browser blocked audio analysis). Speak clearly when recording.',
      };
    }

    if (peak < 3) {
      return {
        ok: false,
        detail: 'No input detected. Speak or clap near the mic during the test, then tap Retry.',
      };
    }

    return { ok: true, detail: 'Microphone is picking up sound.' };
  } catch {
    return {
      ok: true,
      detail: 'Microphone access OK (level test timed out). Speak clearly when recording starts.',
    };
  }
}

async function checkSpeakerOutput(): Promise<{ ok: boolean; detail?: string }> {
  let audioContext: AudioContext | null = null;
  try {
    audioContext = createAudioContext();
    const running = await ensureAudioContextRunning(audioContext);
    if (!running) {
      await audioContext.close().catch(() => {});
      return {
        ok: true,
        detail: 'Speaker test skipped (browser blocked audio). Check volume before recording.',
      };
    }

    const oscillator = audioContext.createOscillator();
    const gain = audioContext.createGain();
    gain.gain.value = 0.08;
    oscillator.frequency.value = 440;
    oscillator.type = 'sine';
    oscillator.connect(gain);
    gain.connect(audioContext.destination);
    oscillator.start();

    await delay(450);

    oscillator.stop();
    await delay(50);
    await audioContext.close();

    return { ok: true, detail: 'Test tone played. Check your volume if you heard nothing.' };
  } catch {
    if (audioContext && audioContext.state !== 'closed') {
      await audioContext.close().catch(() => {});
    }
    return { ok: false, detail: 'Could not play test sound.' };
  }
}

/**
 * Run pre-record checks.
 * Pass micPromise from beginMicrophoneRequest() called inside the click handler.
 */
export async function runRecordingAudioChecks(
  onStepUpdate: (steps: AudioCheckStep[]) => void,
  options: RunAudioChecksOptions
): Promise<{ passed: boolean; steps: AudioCheckStep[] }> {
  let steps: AudioCheckStep[] = INITIAL_AUDIO_CHECK_STEPS.map(s => ({ ...s }));

  const update = (id: string, patch: Partial<AudioCheckStep>): AudioCheckStep[] => {
    steps = steps.map(s => (s.id === id ? { ...s, ...patch } : s));
    onStepUpdate([...steps]);
    return steps;
  };

  onStepUpdate([...steps]);

  let micStream: MediaStream | null = null;

  try {
    // 1. Browser — synchronous
    steps = update('browser', { status: 'running' });
    const browser = checkBrowserSupport();
    if (!browser.ok) {
      steps = update('browser', { status: 'failed', detail: browser.detail });
      // Caller may have already started micPromise — stop it if it resolves later
      void options.micPromise
        .then(stream => stream.getTracks().forEach(t => t.stop()))
        .catch(() => {});
      return { passed: false, steps };
    }
    steps = update('browser', { status: 'passed', detail: browser.detail });

    // 2. Microphone — await the gesture-tied promise
    steps = update('microphone', {
      status: 'running',
      detail: 'Opening microphone…',
    });
    const mic = await resolveMicrophoneStream(options.micPromise);
    if (!mic.ok || !mic.stream) {
      steps = update('microphone', { status: 'failed', detail: mic.detail });
      return { passed: false, steps };
    }
    micStream = mic.stream;
    steps = update('microphone', { status: 'passed', detail: mic.detail });
    await delay(150);

    // 3. Mic level
    steps = update('mic-level', {
      status: 'running',
      detail: 'Listening for ~3.5s — speak or clap near your microphone now.',
    });
    const level = await checkMicrophoneLevel(micStream);
    micStream.getTracks().forEach(t => t.stop());
    micStream = null;
    if (!level.ok) {
      steps = update('mic-level', { status: 'failed', detail: level.detail });
      return { passed: false, steps };
    }
    steps = update('mic-level', { status: 'passed', detail: level.detail });
    await delay(150);

    // 4. Speaker
    steps = update('speaker', { status: 'running', detail: 'Playing test tone…' });
    const speaker = await checkSpeakerOutput();
    if (!speaker.ok) {
      steps = update('speaker', { status: 'failed', detail: speaker.detail });
      return { passed: false, steps };
    }
    steps = update('speaker', { status: 'passed', detail: speaker.detail });

    return { passed: true, steps };
  } finally {
    micStream?.getTracks().forEach(t => t.stop());
  }
}
